"use client";

import { useState, useEffect, useCallback } from "react";

const BREAKPOINTS = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
  widescreen: 1216,
  fullhd: 1408,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

const getBreakpoint = (width: number): Breakpoint => {
  if (width >= BREAKPOINTS.fullhd) return "fullhd";
  if (width >= BREAKPOINTS.widescreen) return "widescreen";
  if (width >= BREAKPOINTS.desktop) return "desktop";
  if (width >= BREAKPOINTS.tablet) return "tablet";
  return "mobile";
};

export const useBreakpoint = (): Breakpoint | null => {
  const [mounted, setMounted] = useState(false);
  const [breakpoint, setBreakpoint] = useState<Breakpoint>("mobile");

  const handleResize = useCallback(() => {
    setBreakpoint(getBreakpoint(window.innerWidth));
  }, []);

  useEffect(() => {
    setMounted(true);
    handleResize();

    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedResize);
    };
  }, [handleResize]);

  return mounted ? breakpoint : null;
};
